import { Search } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import { HelpArticleMeta, groupAdminArticles, helpArticlePath, helpArticles } from './helpCenter';
import { buildSearchIndex, searchHelp } from './search';
import { useHelpContent } from './useHelpContent';
import HelpLoadState from './HelpLoadState';
import { useHelpArticleVisibility } from './helpVisibility';

const cardClass = 'bg-text-contrast rounded-[5px] shadow-sm';
const linkClass = 'text-sm text-foreground hover:text-primary hover:underline underline-offset-2';
const stepNumberClass = 'w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0';

const ArticleLink = ({ article, className }: { article: HelpArticleMeta, className?: string }) => (
    <Link className={className ?? linkClass} to={helpArticlePath(article.slug)}>
        {article.title}
    </Link>
);

const SectionHeading = ({ title, hint }: { title: string, hint: string }) => (
    <div className={'mb-3'}>
        <h2 className={'text-lg font-semibold text-foreground'}>{title}</h2>
        <p className={'text-sm text-foreground/60'}>{hint}</p>
    </div>
);

const HelpCenterPage = () => {
    const { t, i18n } = useTranslation();
    const [params, setParams] = useSearchParams();
    const query = params.get('q') ?? '';
    const { content, loading, failed, retry } = useHelpContent();

    const isVisible = useHelpArticleVisibility();
    const visibleArticles = useMemo(() => helpArticles.filter(isVisible), [isVisible]);
    const searchIndex = useMemo(
        () => (content ? buildSearchIndex(visibleArticles, content) : null),
        [content, visibleArticles],
    );
    const results = useMemo(
        () => (searchIndex && query.trim() ? searchHelp(searchIndex, query) : null),
        [searchIndex, query],
    );

    const onQueryChange = (value: string) => {
        const next = new URLSearchParams(params);
        if (value) next.set('q', value);
        else next.delete('q');
        setParams(next, { replace: true });
    };

    const einstieg = visibleArticles.filter((a) => a.section === 'einstieg');
    const grundlagen = visibleArticles.filter((a) => a.section === 'grundlagen');
    const app = visibleArticles.filter((a) => a.section === 'app');
    const { areas, unclaimed } = useMemo(() => groupAdminArticles(visibleArticles), [visibleArticles]);

    return (
        <div className={'pb-10'}>
            <PageHeader>
                <PageHeadline>{t('help.title')}</PageHeadline>
            </PageHeader>

            <p className={'text-sm text-foreground/70 mb-1'}>{t('help.intro')}</p>
            {i18n.language?.startsWith('en') && <p className={'text-xs text-foreground/50 mb-1'}>{t('help.german_only')}</p>}

            <label className={'relative block mt-5 mb-8 max-w-2xl'}>
                <span className={'sr-only'}>{t('help.search_label')}</span>
                <Search className={'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40'} />
                <input
                    type={'search'}
                    value={query}
                    autoFocus
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder={t('help.search_placeholder')}
                    className={cardClass + ' w-full pl-9 pr-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40'}
                />
            </label>

            {query.trim() ? (
                <section className={'max-w-3xl'} aria-live={'polite'}>
                    <HelpLoadState loading={loading} failed={failed} retry={retry} />
                    {results && (
                        <>
                            <p className={'text-xs text-foreground/50 mb-3'}>{t('help.results', { count: results.length })}</p>
                            {results.length === 0 ? (
                                <p className={'text-sm text-foreground/70'}>{t('help.no_results')}</p>
                            ) : (
                                <ul className={'flex flex-col gap-2'}>
                                    {results.map((r) => (
                                        <li key={r.article.slug} className={cardClass + ' p-4'}>
                                            <Link
                                                to={helpArticlePath(r.article.slug, r.anchor)}
                                                className={'text-sm font-semibold text-foreground hover:text-primary'}
                                            >
                                                {r.article.title}
                                                {r.headingText && <span className={'font-normal text-foreground/60'}>{` › ${r.headingText}`}</span>}
                                            </Link>
                                            <p className={'text-xs text-foreground/40 mt-0.5'}>
                                                {t(`help.sections.${r.article.section}`)} · {t(`help.types.${r.article.type}`)}
                                            </p>
                                            <p className={'text-xs text-foreground/70 mt-1 line-clamp-2'}>{r.snippet}</p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </section>
            ) : (
                <div className={'flex flex-col gap-10'}>
                    <section>
                        <SectionHeading title={t('help.sections.einstieg')} hint={t('help.section_hints.einstieg')} />
                        <ol className={'grid gap-2 sm:grid-cols-2 xl:grid-cols-4'}>
                            {einstieg.map((a, i) => (
                                <li key={a.slug} className={cardClass + ' p-4 flex gap-3 items-start'}>
                                    <span className={stepNumberClass}>
                                        {i + 1}
                                    </span>
                                    <ArticleLink article={a} />
                                </li>
                            ))}
                        </ol>
                    </section>

                    <section>
                        <SectionHeading title={t('help.sections.admin')} hint={t('help.section_hints.admin')} />
                        <div className={'grid gap-3 md:grid-cols-2 xl:grid-cols-3'}>
                            {areas.map(({ area, tasks }) => (
                                <div key={area.slug} className={cardClass + ' p-4'}>
                                    <ArticleLink article={area} className={'text-base font-semibold text-foreground hover:text-primary'} />
                                    {tasks.length > 0 && (
                                        <ul className={'mt-2 flex flex-col gap-1'}>
                                            {tasks.map((task) => (
                                                <li key={task.slug}>
                                                    <ArticleLink article={task} />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                            {unclaimed.length > 0 && (
                                <div className={cardClass + ' p-4'}>
                                    <p className={'text-base font-semibold text-foreground'}>{t('help.more_tasks')}</p>
                                    <ul className={'mt-2 flex flex-col gap-1'}>
                                        {unclaimed.map((task) => (
                                            <li key={task.slug}>
                                                <ArticleLink article={task} />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </section>

                    <section>
                        <SectionHeading title={t('help.sections.grundlagen')} hint={t('help.section_hints.grundlagen')} />
                        <ul className={cardClass + ' p-4 grid gap-2 sm:grid-cols-2'}>
                            {grundlagen.map((a) => (
                                <li key={a.slug}>
                                    <ArticleLink article={a} />
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section>
                        <SectionHeading title={t('help.sections.app')} hint={t('help.section_hints.app')} />
                        <ul className={cardClass + ' p-4 grid gap-2 sm:grid-cols-2'}>
                            {app.map((a) => (
                                <li key={a.slug}>
                                    <ArticleLink article={a} />
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            )}
        </div>
    );
};

export default HelpCenterPage;
